import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, Play, Pause, SkipForward, SkipBack, Volume2, 
  Loader2, Droplets, Sparkles, ChevronRight
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";

const GuidedPracticesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { meditationId } = useParams();
  
  const [meditations, setMeditations] = useState([]);
  const [selectedMeditation, setSelectedMeditation] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [audioError, setAudioError] = useState(null);
  
  const audioRef = useRef(null);
  const backgroundAudioRef = useRef(null);

  useEffect(() => {
    fetchMeditations();
  }, []);

  useEffect(() => {
    if (meditationId && meditations.length > 0) {
      const meditation = meditations.find(m => m.id === meditationId);
      if (meditation) {
        setSelectedMeditation(meditation);
      }
    }
  }, [meditationId, meditations]);

  const fetchMeditations = async () => {
    try {
      const response = await axios.get(`${API}/guided-meditations`);
      setMeditations(response.data.meditations);
    } catch (error) {
      console.error("Error fetching guided meditations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectMeditation = (meditation) => {
    setSelectedMeditation(meditation);
    setCurrentSectionIndex(0);
    setIsPlaying(false);
    setAudioError(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    navigate(`/guided-practices/${meditation.id}`);
  };

  const playSection = async (sectionIndex) => {
    setIsGeneratingAudio(true);
    setAudioError(null);
    
    try {
      // Get audio for this section
      const audioUrl = `${API}/guided-meditations/${selectedMeditation.id}/audio?section_index=${sectionIndex}`;
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.load();
        
        // Start background audio based on soundscape
        startBackgroundAudio(selectedMeditation.soundscape);
        
        audioRef.current.oncanplaythrough = () => {
          setIsGeneratingAudio(false);
          audioRef.current.play();
          setIsPlaying(true);
        };
        
        audioRef.current.onerror = () => {
          setIsGeneratingAudio(false);
          setAudioError("Could not load audio. Please try again.");
        };
        
        audioRef.current.onended = () => {
          // Auto-advance to next section
          if (sectionIndex < selectedMeditation.sections.length - 1) {
            setTimeout(() => {
              setCurrentSectionIndex(sectionIndex + 1);
              playSection(sectionIndex + 1);
            }, 2000); // 2 second pause between sections
          } else {
            setIsPlaying(false);
            stopBackgroundAudio();
          }
        };
      }
    } catch (error) {
      console.error("Error playing audio:", error);
      setIsGeneratingAudio(false);
      setAudioError("Failed to generate audio. Please try again.");
    }
  };

  const startBackgroundAudio = (soundscape) => {
    // Background audio URLs (royalty-free ambient sounds)
    const backgroundSounds = {
      "bells_piano": "https://cdn.pixabay.com/download/audio/2022/03/10/audio_2dde668d05.mp3", // Peaceful piano
      "water_falling": "https://cdn.pixabay.com/download/audio/2022/03/15/audio_942736da05.mp3" // Rain sounds
    };
    
    if (backgroundAudioRef.current && backgroundSounds[soundscape]) {
      backgroundAudioRef.current.src = backgroundSounds[soundscape];
      backgroundAudioRef.current.volume = 0.15; // Low volume background
      backgroundAudioRef.current.loop = true;
      backgroundAudioRef.current.play().catch(() => {
        // Background audio failed to play - not critical
        console.log("Background audio unavailable");
      });
    }
  };

  const stopBackgroundAudio = () => {
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.pause();
      backgroundAudioRef.current.currentTime = 0;
    }
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      backgroundAudioRef.current?.pause();
      setIsPlaying(false);
    } else {
      if (audioRef.current?.src) {
        audioRef.current.play();
        backgroundAudioRef.current?.play();
        setIsPlaying(true);
      } else {
        playSection(currentSectionIndex);
      }
    }
  };

  const handleNext = () => {
    if (currentSectionIndex < selectedMeditation.sections.length - 1) {
      const nextIndex = currentSectionIndex + 1;
      setCurrentSectionIndex(nextIndex);
      if (isPlaying || audioRef.current?.src) {
        playSection(nextIndex);
      }
    }
  };

  const handlePrevious = () => {
    if (currentSectionIndex > 0) {
      const prevIndex = currentSectionIndex - 1;
      setCurrentSectionIndex(prevIndex);
      if (isPlaying || audioRef.current?.src) {
        playSection(prevIndex);
      }
    }
  };

  const handleBack = () => {
    if (selectedMeditation) {
      setSelectedMeditation(null);
      setIsPlaying(false);
      stopBackgroundAudio();
      if (audioRef.current) {
        audioRef.current.pause();
      }
      navigate("/guided-practices");
    } else {
      navigate("/dashboard");
    }
  };

  const getIconForMeditation = (id) => {
    if (id === "the-sink") return <Sparkles className="w-8 h-8" />;
    if (id === "the-shower") return <Droplets className="w-8 h-8" />;
    return <Sparkles className="w-8 h-8" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center" data-testid="guided-practices-loading">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  // Show meditation player
  if (selectedMeditation) {
    const currentSection = selectedMeditation.sections[currentSectionIndex];
    
    return (
      <div className="min-h-screen hero-gradient" data-testid="guided-practice-player">
        {/* Hidden audio elements */}
        <audio ref={audioRef} />
        <audio ref={backgroundAudioRef} />
        
        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground"
              data-testid="guided-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              {getIconForMeditation(selectedMeditation.id)}
              <span className="font-heading text-lg font-semibold">Guided Practice</span>
            </div>
            <div className="w-20"></div>
          </div>
        </nav>

        <main className="max-w-2xl mx-auto px-6 py-8">
          {/* Meditation Title */}
          <div className="text-center mb-8">
            <span className="text-sm text-primary uppercase tracking-wide">{selectedMeditation.category}</span>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2 mb-3">
              {selectedMeditation.title}
            </h1>
            <p className="text-muted-foreground">{selectedMeditation.description}</p>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {selectedMeditation.sections.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentSectionIndex(index);
                  if (isPlaying) playSection(index);
                }}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentSectionIndex 
                    ? "bg-primary scale-125" 
                    : index < currentSectionIndex 
                      ? "bg-primary/50" 
                      : "bg-muted"
                }`}
                aria-label={`Go to section ${index + 1}`}
              />
            ))}
          </div>

          {/* Current Section Card */}
          <Card className="glass-card rounded-3xl border-0 mb-8" data-testid="current-section-card">
            <CardContent className="p-8 md:p-10">
              <div className="text-center mb-6">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  Section {currentSectionIndex + 1} of {selectedMeditation.sections.length}
                </span>
                <h2 className="font-heading text-2xl font-semibold text-foreground mt-2">
                  {currentSection.title}
                </h2>
              </div>
              
              <p className="text-foreground/90 text-lg leading-relaxed text-center italic">
                {currentSection.text}
              </p>

              {audioError && (
                <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm text-center">
                  {audioError}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audio Controls */}
          <div className="flex items-center justify-center gap-6 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevious}
              disabled={currentSectionIndex === 0 || isGeneratingAudio}
              className="w-12 h-12 rounded-full"
              aria-label="Previous section"
            >
              <SkipBack className="w-6 h-6" />
            </Button>
            
            <Button
              onClick={handlePlayPause}
              disabled={isGeneratingAudio}
              className="w-20 h-20 rounded-full bg-primary hover:bg-primary/90 text-white shadow-lg"
              aria-label={isPlaying ? "Pause" : "Play"}
              data-testid="play-pause-btn"
            >
              {isGeneratingAudio ? (
                <Loader2 className="w-10 h-10 animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-10 h-10" />
              ) : (
                <Play className="w-10 h-10 ml-1" />
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              disabled={currentSectionIndex === selectedMeditation.sections.length - 1 || isGeneratingAudio}
              className="w-12 h-12 rounded-full"
              aria-label="Next section"
            >
              <SkipForward className="w-6 h-6" />
            </Button>
          </div>

          {/* Soundscape Info */}
          <div className="text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Volume2 className="w-4 h-4" />
            <span>{selectedMeditation.soundscape_description}</span>
          </div>
        </main>
      </div>
    );
  }

  // Show meditation list
  return (
    <div className="min-h-screen hero-gradient" data-testid="guided-practices-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Guided Practices</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
            Guided Audio Practices
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Immersive, narrated meditations to bring mindfulness into your everyday moments
          </p>
        </div>

        {/* Meditation Cards */}
        <div className="grid gap-6">
          {meditations.map((meditation) => (
            <Card
              key={meditation.id}
              className="glass-card rounded-3xl border-0 cursor-pointer card-hover overflow-hidden"
              onClick={() => handleSelectMeditation(meditation)}
              data-testid={`meditation-card-${meditation.id}`}
            >
              <CardContent className="p-0">
                <div className="flex items-stretch">
                  {/* Icon Side */}
                  <div className={`w-32 flex items-center justify-center ${
                    meditation.id === "the-sink" 
                      ? "bg-gradient-to-br from-primary/20 to-primary/5" 
                      : "bg-gradient-to-br from-accent/20 to-accent/5"
                  }`}>
                    <div className={`text-${meditation.id === "the-sink" ? "primary" : "accent"}`}>
                      {getIconForMeditation(meditation.id)}
                    </div>
                  </div>
                  
                  {/* Content Side */}
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-xs text-primary uppercase tracking-wide">
                          {meditation.category}
                        </span>
                        <h2 className="font-heading text-xl font-semibold text-foreground mt-1 mb-2">
                          {meditation.title}
                        </h2>
                        <p className="text-muted-foreground text-sm mb-3">
                          {meditation.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{meditation.duration}</span>
                          <span>•</span>
                          <span>{meditation.sections.length} sections</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Coming Soon Note */}
        <div className="text-center mt-12 py-8">
          <p className="font-handwritten text-xl text-primary">
            More guided practices coming soon...
          </p>
        </div>
      </main>
    </div>
  );
};

export default GuidedPracticesPage;
